import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getLocationBySlug, locationFaqs, locations } from "@/data/locations";
import { courseCategories, getCoursesByCategory } from "@/data/courses";
import { MapPin, BookOpen, Phone, MessageCircle, ChevronRight, ArrowRight, GraduationCap, Landmark, Shield, Train, ShieldCheck, FileText, Trophy, Star, Users, Clock, CheckCircle, HelpCircle, ImageIcon } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const categoryIcons = { banking: Landmark, ssc: Shield, railway: Train, mba: GraduationCap, insurance: ShieldCheck, others: FileText };
const categoryColors = { banking: "from-blue-600 to-indigo-600", ssc: "from-vedanti-orange to-red-500", railway: "from-amber-600 to-vedanti-mango", mba: "from-emerald-600 to-teal-500", insurance: "from-purple-600 to-pink-500", others: "from-vedanti-navy to-blue-600" };

const nearbyToSlug = (name) => {
  const m = locations.find((l) => l.name.toLowerCase() === name.toLowerCase());
  return m ? m.slug : null;
};

export default function LocationPage() {
  const { locationSlug } = useParams();
  const location = getLocationBySlug(locationSlug);

  if (!location) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <h1 className="text-3xl font-bold text-vedanti-navy mb-4">Location Not Found</h1>
        <Link to="/" className="text-vedanti-orange hover:underline">Go to Homepage</Link>
      </div>
    );
  }

  const faqs = locationFaqs(location.name);
  const childLocalities = locations.filter((l) => l.parentTown === location.slug);
  const pageTitle = `Best Exam Coaching in ${location.name} | Vedanti Academy`;
  const pageDescription = `Vedanti Academy - Top coaching institute near ${location.name} for IBPS, SBI, SSC CGL, MBA CET, Railway NTPC & Insurance exams. 500+ Google reviews, 5-star rated. ${location.distance} from our branch. Join now!`;

  return (
    <div data-testid={`location-page-${location.slug}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`coaching in ${location.name}, banking coaching ${location.name}, SSC coaching ${location.name}, MBA CET coaching ${location.name}, competitive exam classes ${location.name}, Vedanti Academy ${location.name}`} />
        <meta property="og:title" content={`Best Coaching in ${location.name} | Vedanti Academy`} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://vedantiacademy.com/locations/${location.slug}`} />
        <link rel="canonical" href={`https://vedantiacademy.com/locations/${location.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": `Vedanti Academy - ${location.name}`,
          "description": pageDescription,
          "url": `https://vedantiacademy.com/locations/${location.slug}`,
          "areaServed": { "@type": "Place", "name": location.name },
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "500", "bestRating": "5" },
          "telephone": "+918308776505"
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-vedanti-navy"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.2)_0%,_transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(255,195,0,0.1)_0%,_transparent_50%)]"></div>
        <div className="absolute top-8 right-16 w-72 h-72 bg-vedanti-orange/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main py-14 md:py-20 relative z-10">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
            <Link to="/" className="hover:text-white transition">Home</Link>
            <ChevronRight size={14} />
            <Link to="/courses" className="hover:text-white transition">Locations</Link>
            <ChevronRight size={14} />
            <span className="text-vedanti-mango">{location.name}</span>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-gradient-to-br from-vedanti-orange to-vedanti-mango rounded-xl flex items-center justify-center shadow-lg">
              <MapPin size={24} className="text-white" />
            </div>
            <span className="text-vedanti-mango font-bold text-sm uppercase tracking-[0.15em]">{location.tagline}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
            Best Coaching in <span className="text-transparent bg-clip-text bg-gradient-to-r from-vedanti-orange to-vedanti-mango">{location.name}</span>
          </h1>
          <p className="text-slate-300 mt-4 text-lg max-w-3xl">{location.description}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/demo-class" className="bg-gradient-to-r from-vedanti-orange to-amber-500 text-white px-7 py-3.5 rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all inline-flex items-center gap-2" data-testid="location-demo-btn">
              Book Free Demo Class <ArrowRight size={18} />
            </Link>
            <a href="tel:+918308776505" className="border-2 border-white/30 text-white hover:bg-white/10 px-7 py-3.5 rounded-xl font-semibold transition-all inline-flex items-center gap-2">
              <Phone size={18} /> +91 8308776505
            </a>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-white border-b">
        <div className="container-main py-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {[
              { icon: Trophy, value: "31+", label: "Achievers", color: "text-vedanti-orange" },
              { icon: Star, value: "5.0", label: "Google Rating", color: "text-vedanti-mango" },
              { icon: Users, value: "500+", label: "Reviews", color: "text-blue-600" },
              { icon: BookOpen, value: "30+", label: "Courses", color: "text-emerald-600" },
              { icon: Clock, value: location.distance, label: "From Branch", color: "text-purple-600" },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50" data-testid={`stat-${i}`}>
                  <Icon size={22} className={stat.color} />
                  <div>
                    <div className={`font-heading font-extrabold text-lg ${stat.color}`}>{stat.value}</div>
                    <div className="text-slate-500 text-xs">{stat.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Vedanti */}
      <section className="section-padding bg-gradient-to-b from-white to-slate-50">
        <div className="container-main">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-vedanti-navy mb-3">
            Why Students from <span className="text-vedanti-orange">{location.name}</span> Choose Vedanti Academy
          </h2>
          <p className="text-slate-600 mb-8 max-w-3xl text-base md:text-lg">{location.whyChoose}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "AC Classrooms with Digital Boards", desc: "Modern learning environment with smart boards and air conditioning for focused study sessions." },
              { title: "360-Degree Hybrid Doubt Support", desc: "Get doubts cleared offline in class and online via WhatsApp/Zoom throughout the week." },
              { title: "Upto Selection Support", desc: "We support you until you get selected — no fixed course end dates, unlimited attempts." },
              { title: "Shortcuts & Vedic Math", desc: "Learn unique shortcuts and Vedic Math techniques to solve problems faster in exams." },
              { title: "In-House Library", desc: "Access our library with all competitive exam books, previous papers, and reference material." },
              { title: `Easy Access from ${location.name}`, desc: location.connectivity || `Convenient transport connectivity from ${location.name} to our branches.` },
            ].map((item) => (
              <div key={item.title} className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle size={20} className="text-vedanti-orange mb-2" />
                <h3 className="font-bold text-vedanti-navy text-base mb-1">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Course Categories */}
      <section className="section-padding bg-slate-50" data-testid="courses-section">
        <div className="container-main">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-vedanti-navy text-center mb-3">
            Courses Available for <span className="text-vedanti-orange">{location.name}</span> Students
          </h2>
          <p className="text-slate-500 text-center mb-12 max-w-2xl mx-auto">Comprehensive coaching for 30+ competitive exams — all accessible to students from {location.name}</p>

          {courseCategories.map((category) => {
            const catCourses = getCoursesByCategory(category.id);
            const Icon = categoryIcons[category.id] || BookOpen;
            const gradient = categoryColors[category.id] || "from-vedanti-navy to-blue-600";
            return (
              <div key={category.id} className="mb-12 last:mb-0">
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                    <Icon size={18} className="text-white" />
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-vedanti-navy">{category.name}</h3>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {catCourses.map((course) => (
                    <Link
                      key={course.slug}
                      to={`/locations/${location.slug}/${course.slug}`}
                      className="bg-white p-4 rounded-xl border border-slate-100 hover:border-vedanti-orange/30 shadow-sm hover:shadow-md transition-all group"
                      data-testid={`course-card-${course.slug}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-bold text-vedanti-navy group-hover:text-vedanti-orange transition-colors text-base">{course.name}</h4>
                          <p className="text-slate-500 text-xs mt-1 line-clamp-2">{course.fullName}</p>
                        </div>
                        <ArrowRight size={16} className="text-slate-300 group-hover:text-vedanti-orange transition-colors mt-1 flex-shrink-0" />
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-50">
                        <span className="text-xs text-slate-400">{course.eligibility?.split(".")[0]}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Programs CTA — internal SEO interlinking */}
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="location-programs-grid">
            {[
              `best-banking-coaching-in-${location.slug}`,
              `best-ssc-coaching-in-${location.slug}`,
              `best-mba-coaching-in-${location.slug}`,
              `best-ibps-po-coaching-in-${location.slug}`,
              `best-ssc-cgl-coaching-in-${location.slug}`,
              `online-mah-mba-cet-coaching-in-${location.slug}`,
              `quantitative-aptitude-coaching-in-${location.slug}`,
              `reasoning-ability-coaching-in-${location.slug}`,
              `how-to-crack-ibps-po-from-${location.slug}`,
            ].map((s) => (
              <Link key={s} to={`/programs/${s}`} className="bg-orange-50 hover:bg-vedanti-orange hover:text-white border border-vedanti-orange/20 rounded-lg p-3 text-sm text-vedanti-navy font-medium transition-all flex items-center justify-between gap-2">
                <span>{s.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()).replace(/Ibps|Sbi|Rbi|Rrb|Lic|Gic|Ssc|Cgl|Chsl|Mts|Cmat|Snap|Mat|Mba|Cet/gi, (m) => m.toUpperCase())}</span>
                <ArrowRight size={14} className="flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="relative overflow-hidden" data-testid="gallery-cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-vedanti-navy via-[#001a3a] to-vedanti-navy"></div>
        <div className="container-main py-14 md:py-16 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ImageIcon size={20} className="text-vedanti-mango" />
                <span className="text-vedanti-mango font-bold text-sm uppercase tracking-wide">Wall of Fame</span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white">See Our <span className="text-vedanti-orange">Achievers</span> from {location.name} Region</h2>
              <p className="text-slate-400 mt-2 max-w-lg">Real students, real results — browse our gallery of 31+ achievers who cracked Banking, SSC, MBA, and Railway exams through Vedanti Academy&apos;s coaching.</p>
            </div>
            <Link
              to="/gallery"
              className="bg-gradient-to-r from-vedanti-orange to-amber-500 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all inline-flex items-center gap-2 whitespace-nowrap"
              data-testid="gallery-link"
            >
              <Trophy size={18} /> View Achievers Gallery <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Nearby Areas */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-vedanti-navy mb-6">
            Also Serving Students from Nearby Areas
          </h2>
          <div className="flex flex-wrap gap-3">
            {location.nearbyAreas.map((area) => {
              const match = nearbyToSlug(area);
              return match ? (
                <Link key={area} to={`/locations/${match}`} className="bg-slate-100 hover:bg-vedanti-orange hover:text-white text-slate-700 px-4 py-2 rounded-full text-sm font-medium transition-all">{area}</Link>
              ) : (
                <span key={area} className="bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium">{area}</span>
              );
            })}
          </div>

          {/* Sub-localities — internal-link hub for the parent town */}
          {childLocalities.length > 0 && (
            <div className="mt-10" data-testid="child-localities">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-vedanti-navy mb-2">
                Localities &amp; Chowks We Serve in {location.name}
              </h3>
              <p className="text-slate-500 text-sm mb-5">Hyperlocal coaching for every {location.name} neighbourhood — click your area to see courses, fees and batches near you.</p>
              <div className="flex flex-wrap gap-2">
                {childLocalities.map((c) => (
                  <Link key={c.slug} to={`/locations/${c.slug}`} className="text-sm px-3 py-1.5 rounded-full bg-orange-50 border border-vedanti-orange/20 text-vedanti-navy hover:bg-vedanti-orange hover:text-white font-medium transition-all" data-testid={`child-locality-${c.slug}`}>
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-slate-50" data-testid="faq-section">
        <div className="container-main max-w-4xl">
          <div className="flex items-center gap-3 mb-8">
            <HelpCircle size={28} className="text-vedanti-orange" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-vedanti-navy">
              FAQs for <span className="text-vedanti-orange">{location.name}</span> Students
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={faq.q} value={`faq-${i}`} className="bg-white rounded-xl border border-slate-100 px-5 shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-vedanti-navy hover:text-vedanti-orange text-base py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-vedanti-orange via-amber-500 to-vedanti-mango"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(255,255,255,0.15)_0%,_transparent_50%)]"></div>
        <div className="container-main py-14 md:py-16 relative z-10 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-extrabold text-white mb-3">Start Your Preparation from {location.name} Today!</h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">Join 31+ successful achievers from the region. Book a free demo class or consultancy now.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/demo-class" className="bg-white text-vedanti-navy hover:bg-vedanti-navy hover:text-white rounded-xl px-7 py-3.5 font-bold shadow-xl hover:-translate-y-0.5 transition-all" data-testid="cta-demo-btn">
              Book Free Demo Class
            </Link>
            <a href={`https://wa.me/918308776505?text=${encodeURIComponent(`Hi, I am from ${location.name} and want to know about coaching.`)}`} target="_blank" rel="noopener noreferrer nofollow" className="border-2 border-white text-white hover:bg-white hover:text-green-600 rounded-xl px-7 py-3.5 font-semibold transition-all inline-flex items-center gap-2" data-testid="cta-whatsapp-btn">
              <MessageCircle size={18} /> WhatsApp Us
            </a>
            <Link to="/consultancy" className="border-2 border-white text-white hover:bg-white hover:text-vedanti-orange rounded-xl px-7 py-3.5 font-semibold transition-all" data-testid="cta-consult-btn">
              Free Consultancy
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
